import {
    getEligibility,
    parsePhoneNumberAttributes,
    useSalesContext,
    useVerifiedCustomerStore,
} from "@expert/sales-tools";
import { useEffect, useEffectEvent } from "react";

import { getReservationSid } from "@/sales-tools/utils/getReservationSid";
import { getMostRecentTask, getSession, isSimulatedVoiceTask, isVoiceTask, useLogger } from "@/sdk";

/*
 * Experts try to scam the system without this. Traditionally, we only check eligibility after an expert confirms
 * the callers phone number. Experts figured this out and stopped confirming the customers number bc they don't want
 * to sell
 *
 * We delay the check by 10 seconds to account for EVL connection issues. If an expert has bad audio, the call briefly
 * connects and this check will fire. Then the call will disconnect. This unfairly performs an eligibility check. By
 * delaying 10 seconds, we do not perform a check in the case of audio issues
 *
 * This check runs 10 seconds after the Customer Panel mounts.
 */
export const useEligibilityPrecheck = (sessionId: string) => {
    const logger = useLogger({ module: "useEligibilityPrecheck", supportTeam: "sales" });
    const { apiClients, analytics } = useSalesContext();

    const schedulePrecheck = useEffectEvent(() => {
        const session = getSession(sessionId);

        if (!session) {
            logger.debug("No session found for task, skipping eligibility precheck");
            return undefined;
        }

        const task = getMostRecentTask(session);
        if (!isVoiceTask(task) || isSimulatedVoiceTask(task)) {
            logger.debug("No voice task, skipping eligibility precheck");
            return undefined;
        }

        logger.debug({ sessionId: session.id }, "Eligibility precheck scheduled for session");

        return setTimeout(() => {
            logger.debug({ sessionId: session.id }, "Eligibility precheck firing for session");

            if (useVerifiedCustomerStore.getState().getVerifiedCustomer(session.id).isCustomerVerificationComplete) {
                return;
            }

            void getEligibility(apiClients.sales, logger, analytics, {
                customerInteractionId: task.asurionCallId,
                partner: task.partner,
                reservationSid: getReservationSid(task) ?? session.id,
                sessionId: session.id,
                ...parsePhoneNumberAttributes(task?.mdn),
                salesChannel: task?.type,
                eligibilityAttributes: {
                    isPrecheck: true,
                },
            });
        }, 10_000);
    });

    useEffect(() => {
        const timeout = schedulePrecheck();
        return () => {
            if (timeout) clearTimeout(timeout);
        };
    }, [sessionId]);
};
